import { css } from '@emotion/react';

import {
  COLOR_BORDER_INPUT,
  COLOR_BRAND,
  COLOR_BRAND_HOVER,
  COLOR_DANGER,
  COLOR_DANGER_BORDER,
  COLOR_DANGER_SOFT,
  COLOR_SURFACE,
  COLOR_SURFACE_MUTED,
  COLOR_TEXT_PRIMARY,
  RADIUS_MD,
} from './variables';

/** Matches shadcn/ui button variants (subset used in admin catalog). */
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'link';

/** Matches shadcn/ui button sizes. */
export type ButtonSize = 'default' | 'sm' | 'lg';

export type ButtonAppearanceOptions = {
  variant?: ButtonVariant;
  size?: ButtonSize;
};

const linkStyles = css`
  display: inline;
  border: none;
  background: none;
  padding: 0;
  font-weight: 600;
  color: ${COLOR_BRAND};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    color: ${COLOR_BRAND_HOVER};
  }
`;

const outlineShell = css`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${COLOR_BORDER_INPUT};
  background: ${COLOR_SURFACE};
  color: ${COLOR_TEXT_PRIMARY};
  border-radius: ${RADIUS_MD};
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background: ${COLOR_SURFACE_MUTED};
    color: ${COLOR_TEXT_PRIMARY};
  }
`;

const sizeStyles = (size: ButtonSize) => {
  switch (size) {
    case 'sm':
      return css`
        justify-content: center;
        box-sizing: border-box;
        padding: 6px 12px;
        font-size: 13px;
        line-height: 1.25;
      `;
    case 'lg':
      return css`
        padding: 11px 20px;
        font-size: 14px;
      `;
    default:
      return css`
        padding: 9px 16px;
        font-size: 14px;
      `;
  }
};

const primaryStyles = (size: ButtonSize) => css`
  border: none;
  background: ${COLOR_BRAND};
  color: ${COLOR_SURFACE};
  border-radius: ${RADIUS_MD};
  font-weight: 600;
  ${sizeStyles(size)}

  &:hover:not(:disabled) {
    background: ${COLOR_BRAND_HOVER};
  }
`;

const secondaryStyles = (size: ButtonSize) => css`
  ${outlineShell}
  ${sizeStyles(size)}
`;

const dangerStyles = (size: ButtonSize) => css`
  ${secondaryStyles(size)}
  color: ${COLOR_DANGER};
  border-color: ${COLOR_DANGER_BORDER};

  &:hover {
    background: ${COLOR_DANGER_SOFT};
  }
`;

export const buttonAppearance = ({ variant = 'secondary', size = 'default' }: ButtonAppearanceOptions) => {
  switch (variant) {
    case 'primary':
      return primaryStyles(size);
    case 'danger':
      return dangerStyles(size);
    case 'link':
      return linkStyles;
    default:
      return secondaryStyles(size);
  }
};

/** `<button>` only — primary variant disabled cursor/opacity. */
export const primaryButtonDisabledAppearance = (disabled?: boolean) => css`
  cursor: ${disabled ? 'not-allowed' : 'pointer'};
  opacity: ${disabled ? 0.65 : 1};
`;
